import { useState, useEffect, useCallback } from "react";

import { OrgSessionEvents } from "@platform/constants/Events";
import { eventBus } from "@platform/events/EventBus";

import { orgSessionManager } from "../services/orgSessionManager";

export interface OrgSessionState {
  orgAlias: string | null;
  orgId: number | null;
  switching: boolean;
  selectOrg: (orgAlias: string) => Promise<void>;
  clearSession: () => void;
}

/**
 * React hook that tracks the current org session.
 *
 * Reads from OrgSessionManager (single source of truth) and subscribes
 * to ORG_SESSION_CHANGED events so the UI updates when the org is switched
 * mid-session without requiring a page reload.
 *
 * The session JWT lives in an httpOnly cookie and is never exposed to JS.
 */
export function useOrgSession(): OrgSessionState {
  const [orgAlias, setOrgAlias] = useState<string | null>(
    orgSessionManager.getOrgAlias(),
  );
  const [orgId, setOrgId] = useState<number | null>(
    orgSessionManager.getOrgId(),
  );
  const [switching, setSwitching] = useState<boolean>(
    orgSessionManager.isSwitching(),
  );

  useEffect(() => {
    const handler = (payload: { orgAlias: string; orgId: number }) => {
      setOrgAlias(payload.orgAlias);
      setOrgId(payload.orgId);
      setSwitching(false);
    };

    eventBus.subscribeGlobal(OrgSessionEvents.ORG_SESSION_CHANGED, handler);

    // Also listen for switch failures to reset the switching flag
    const failHandler = () => {
      setSwitching(false);
    };

    eventBus.subscribeGlobal(OrgSessionEvents.ORG_SWITCH_FAILED, failHandler);

    // No unsubscribe — GlobalEventBus handlers persist for the singleton lifetime,
    // matching the pattern in useAuth.ts and useOrganization.ts
  }, []);

  const selectOrg = useCallback(async (alias: string) => {
    setSwitching(true);
    try {
      await orgSessionManager.selectOrg(alias);
    } catch {
      setSwitching(false);
      throw new Error("Failed to select organization. Please try again.");
    }
  }, []);

  const clearSession = useCallback(() => {
    orgSessionManager.clearSession();
    setOrgAlias(null);
    setOrgId(null);
  }, []);

  return { orgAlias, orgId, switching, selectOrg, clearSession };
}
